import { normalizeLang } from "../_helpers/language";
import { defaultLang, frontendUrl, nativeOAuthProviders } from "../_constants";
import { vcardService } from "../_services/vcard.service";

// Единственная точка контакта с нативной обёрткой мобильного приложения.
// Полный контракт сообщений — в NATIVE_CONTRACT.md рядом с этим файлом.
//
// Главное правило: без обёртки (обычный браузер) ничего не должно ломаться —
// иначе отладку вне приложения делать невозможно. Каждое нативное действие
// имеет веб-фолбэк либо возвращает "unsupported", а вызывающий показывает подсказку.

export const NATIVE_EVENT = "naviconf-native";
export const SYS_LANG_KEY = "navic_sys_lang";
export const PLATFORM_KEY = "nc_platform";
export const APPVER_KEY = "nc_appver";
// Маркер «этот возврат OAuth — для приложения». Ставит /oauth-start в браузере,
// читает и сразу снимает OAuthPage. redirect_uri общий с вебом, отличить возврат
// приложения больше нечем.
export const NATIVE_OAUTH_KEY = "nc_native_oauth";
// Маркер протухает: человек мог начать вход с телефона, бросить, а через неделю
// открыть кабинет с ноутбука в том же браузере — и уехать на схему приложения.
export const NATIVE_OAUTH_TTL = 10 * 60 * 1000;
// Своя схема, не naviconf:// — та занята китайским приложением, и на устройстве
// с обоими возврат уедет не туда.
export const NATIVE_OAUTH_SCHEME = "naviconfcard://oauth";

const PROTOCOL_VERSION = 1;

export function isNativeAvailable() {
  return typeof window !== "undefined" && !!window.ReactNativeWebView;
}

// --- Веб → нативка -------------------------------------------------------

// Возвращает true, если сообщение ушло в обёртку. false — обёртки нет,
// вызывающий должен сам решить, что делать (фолбэк или подсказка).
export function postToNative(type, payload = {}) {
  if (!isNativeAvailable()) return false;
  try {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ v: PROTOCOL_VERSION, type, payload })
    );
    return true;
  } catch (e) {
    return false;
  }
}

// --- Нативка → веб -------------------------------------------------------

// Приёмник объявляет веб, обёртка только зовёт его через injectJavaScript.
export function installNativeReceiver() {
  if (typeof window === "undefined" || window.NaviconfNative) return;

  window.NaviconfNative = {
    emit(msg) {
      let data = msg;
      if (typeof msg === "string") {
        try {
          data = JSON.parse(msg);
        } catch (e) {
          return;
        }
      }
      if (!data || !data.type) return;
      window.dispatchEvent(new CustomEvent(NATIVE_EVENT, { detail: data }));
    },
  };
}

// Подписка на сообщение от обёртки. Возвращает функцию отписки.
export function onNative(type, handler) {
  const listener = (event) => {
    const msg = event.detail;
    if (!msg || msg.type !== type) return;
    handler(msg.payload || {}, msg);
  };
  window.addEventListener(NATIVE_EVENT, listener);
  return () => window.removeEventListener(NATIVE_EVENT, listener);
}

// --- Аппаратная кнопка «назад» ------------------------------------------

// Стек обработчиков: последний зарегистрированный получает событие первым.
// Обработчик возвращает true, если «назад» обработан и дальше идти не надо
// (например, закрыл диалог).
const backHandlers = [];

export function pushBackHandler(handler) {
  backHandlers.push(handler);
  return () => {
    const index = backHandlers.indexOf(handler);
    if (index >= 0) backHandlers.splice(index, 1);
  };
}

export function handleBack() {
  for (let i = backHandlers.length - 1; i >= 0; i--) {
    if (backHandlers[i]() === true) return true;
  }
  return false;
}

// --- Действия с фолбэками ------------------------------------------------

// "native" | "web" | "cancelled" | "unsupported"
export function shareVcard({ vcard, qrDataUrl, text }) {
  if (postToNative("share_vcard", { vcard, qrDataUrl, text })) {
    return Promise.resolve("native");
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    return navigator
      .share({ text: text || vcard })
      .then(() => "web")
      .catch(() => "cancelled");
  }
  return Promise.resolve("unsupported");
}

// Системный экран добавления контакта. В браузере — скачивание .vcf,
// его ОС тоже обычно предлагает открыть в адресной книге.
export function saveContactToPhone(vcard) {
  if (postToNative("save_contact", { vcard })) return "native";
  vcardService.downloadVcf(vcard);
  return "web";
}

export function saveFile({ filename, mime, dataUrl }) {
  if (postToNative("save_file", { filename, mime, dataUrl })) return "native";
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.click();
  return "web";
}

// Сканер только нативный: камеры в вебе мы намеренно не трогаем.
//
// Кнопка сканирования одна и та же для QR и для бумажной визитки: режим выбирает
// камера, а не человек. QR ловится непрерывно, бумажная визитка снимается затвором,
// и ответ приходит либо scan_result, либо card_scanned. Второй круглой кнопки
// на экране нет намеренно — правый нижний угол занят, левый попадает в полосу
// системного жеста «назад», а две одинаковые кнопки рядом в спешке путают.
//
// card разрешает съёмку бумажной визитки и остаётся true всегда: тариф «Умный скан»
// закрывает распознанные поля, а не съёмку (scanTariff.js) — фото сохраняется
// бесплатно, контакт не теряется ни при каком тарифе.
export function requestScan() {
  return postToNative("scan_qr", { card: true });
}

// Снимок удалённого контакта больше не нужен: обёртка стирает рабочую копию,
// которую раздаёт по /cards/. Копию в системной галерее не трогаем — она
// пользовательская, человек сам нажимал затвор.
export function deleteCardPhoto(photo) {
  if (!photo) return false;
  return postToNative("delete_card_photo", { photo });
}

// Вход через провайдера открывается снаружи — приложением провайдера, если оно стоит,
// иначе браузером по умолчанию. Внутри WebView нет ни сессий браузера, ни сохранённых
// паролей: человеку пришлось бы вбивать пароль от Яндекса руками, будучи залогиненным
// и в браузере, и в приложении Яндекса.
//
// Ведём не сразу на бэкенд, а на промежуточную /oauth-start фронт-домена: она в контексте
// браузера кладёт маркер, по которому страница колбэка вернёт человека в приложение.
// Возвращает false (нет обёртки или не задан frontendUrl) — вызывающий идёт обычной ссылкой.
export function startOAuth(provider, type) {
  if (!frontendUrl) return false;
  if (nativeOAuthProviders.indexOf(provider) < 0) return false;

  const url =
    `${frontendUrl}/oauth-start?provider=${encodeURIComponent(provider)}` +
    (type ? `&type=${encodeURIComponent(type)}` : "");

  return postToNative("oauth_start", { url });
}

export function openExternal(url) {
  if (postToNative("open_external", { url })) return "native";
  window.open(url, "_blank");
  return "web";
}

export function notifyReady({ lang, authorized }) {
  return postToNative("ready", { lang, authorized: !!authorized });
}

export function notifyAuthState(authorized) {
  return postToNative("auth_state", { authorized: !!authorized });
}

// Консоль WebView недоступна снаружи — важные ошибки дублируем в нативный лог.
export function logToNative(level, message) {
  return postToNative("log", { level, message: String(message) });
}

// --- Параметры первой загрузки ------------------------------------------

// Обёртка грузит http://localhost:8080/?lang=ru&platform=android&appver=1.2.0
// SPA-навигация query теряет, поэтому раскладываем параметры в localStorage сразу.
//
// Вызывать первым делом в src/index.js: публичные страницы (LoginPage и др.)
// читают localStorage.navic_lang напрямую при рендере.
export function captureBootParams() {
  if (typeof window === "undefined") return;

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return;
  }

  if (params.get("platform")) localStorage.setItem(PLATFORM_KEY, params.get("platform"));
  if (params.get("appver")) localStorage.setItem(APPVER_KEY, params.get("appver"));

  // Явный ?lang= от обёртки всегда перебивает сохранённое значение.
  // navigator.language — только фолбэк на случай, если обёртка ничего не передала:
  // затирать им то, что она уже сообщила, нельзя (в браузере разработчика это
  // локаль машины, а не устройства).
  const explicit = normalizeLang(params.get("lang"));
  if (explicit) {
    localStorage.setItem(SYS_LANG_KEY, explicit);
  } else if (!localStorage.getItem(SYS_LANG_KEY)) {
    const guessed = normalizeLang(navigator.language);
    if (guessed) localStorage.setItem(SYS_LANG_KEY, guessed);
  }
  const sysLang = localStorage.getItem(SYS_LANG_KEY);

  // Системный язык — только стартовое значение. Локаль профиля перебьёт его
  // после загрузки пользователя (CardShell.applyProfileLocale), а уже выбранный
  // ранее язык не трогаем.
  if (!localStorage.getItem("navic_lang")) {
    localStorage.setItem("navic_lang", sysLang || defaultLang || "ru");
  }
}

export function getSystemLang() {
  return localStorage.getItem(SYS_LANG_KEY);
}
